import { Modal, Button, Alert, Spinner } from "react-bootstrap";
import React, { useState, useEffect } from "react";
import "../modal.css";
import "react-datepicker/dist/react-datepicker.css";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import DatePicker from "react-datepicker";
import { useFormik } from "formik";
import * as Yup from "yup";
import Loader from "../Loader";
import PassengersList from "./PassengersList";
import BasicModal from "./BasicModal";

const AddSubscriber = ({
  displayAddModal,
  setDisplayAddModal,
  showModal,
  hideModal,
  getSubscribers,
}) => {
  const [currentCompany, setCurrentCompany] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [showTable, setShowTable] = useState(false);
  const [selectedPassengers, setSelectedPassengers] = useState([]);

  const navigate = useNavigate();
  const params = useParams();

  // Validation schema
  const validationSchema = Yup.object().shape({
    companyId: Yup.string().required("Company ID is required"),
    passengerId: Yup.string().required("At least one passenger must be selected"),
    usageLimit: Yup.number()
      .required("Usage limit is required")
      .min(0, "Usage limit must be at least 0")
      .max(
        currentCompany?.balance || 0,
        `Usage limit cannot exceed company balance of ${currentCompany?.balance || 0}`
      ),
    startDate: Yup.mixed()
      .nullable()
      .test('is-valid-date', 'Invalid start date provided', function(value) {
        if (!value) return true;
        const date = new Date(value);
        if (isNaN(date.getTime())) return false;
        const today = new Date().setHours(0, 0, 0, 0);
        if (date.getTime() < today) {
          return this.createError({ message: 'Start date must be today or later' });
        }
        return true;
      })
      .required("Start date is required"),
    endDate: Yup.mixed()
      .nullable()
      .test('is-valid-date', 'Invalid end date provided', function(value) {
        if (!value) return true;
        const date = new Date(value);
        if (isNaN(date.getTime())) return false;
        const startDate = this.parent.startDate;
        if (startDate && date.getTime() < new Date(startDate).getTime()) {
          return this.createError({ message: 'End date must be later than or equal to start date' });
        }
        return true;
      })
      .required("End date is required"),
    status: Yup.string().required("Status is required"),
  });

  // Formik configuration
  const formik = useFormik({
    initialValues: {
      companyId: "",
      passengerId: "",
      usageLimit: "",
      startDate: null,
      endDate: null,
      status: "",
    },
    validationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      await handleSubmit(values);
      setSubmitting(false);
    },
  });

  // Load company data on component mount
  useEffect(() => {
    if (params.id) {
      getCurrentCompany();
    }
  }, [params.id]);

  // Update passenger validation when selection changes
  useEffect(() => {
    const passengerIds = selectedPassengers.map((passenger) => passenger.id);
    formik.setFieldValue("passengerId", JSON.stringify(passengerIds));
    formik.setFieldValue("companyId", currentCompany?.id || "");
  }, [selectedPassengers, currentCompany]);

  const getCurrentCompany = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/company/${params.id}`);
      setCurrentCompany(response.data.company);
      setErrorMsg("");
    } catch (error) {
      setErrorMsg("Failed to load company data");
      showErrorToast("Failed to load company data");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (values) => {
    try {
      setLoading(true);
      setErrorMsg("");

      // Validate required fields
      if (!values.companyId || !values.passengerId || selectedPassengers.length === 0) {
        setErrorMsg("Please fill all required fields and select at least one passenger");
        return;
      }

      const formData = new FormData();
      Object.keys(values).forEach((key) => {
        if (values[key] !== null && values[key] !== "") {
          formData.append(key, values[key]);
        }
      });

      const response = await axios.post("/company/addSubscriber", formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      // Success handling
      hideModal();
      getSubscribers();
      showSuccessToast("Subscriber added successfully!");
      resetForm();

    } catch (error) {
      const errorMessage = error.response?.data?.msg || "Something went wrong. Please try again.";
      setErrorMsg(errorMessage);
      showErrorToast(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    formik.resetForm();
    setSelectedPassengers([]);
    setErrorMsg("");
    setShowTable(false);
  };

  const handleSelectRows = (selectedRows) => {
    setSelectedPassengers(selectedRows);
    // Trigger validation for passenger selection
    formik.setFieldTouched("passengerId", true);
  };

  const showSuccessToast = (message) => {
    toast.success(message, {
      position: "top-right",
      autoClose: 3000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
    });
  };

  const showErrorToast = (message) => {
    toast.error(message, {
      position: "top-right",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
    });
  };

  const handleCancel = () => {
    resetForm();
    hideModal();
  };

  // Custom DatePicker component with safe date parsing
  const CustomDatePicker = ({ name, label, ...props }) => {
    const hasError = formik.touched[name] && formik.errors[name];
    
    // Safe date parsing function
    const parseDate = (dateValue) => {
      if (!dateValue) return null;
      
      try {
        const date = new Date(dateValue);
        
        // Check if date is valid
        if (isNaN(date.getTime())) {
          console.warn('Invalid date detected in CustomDatePicker:', dateValue);
          return null;
        }
        
        // Check if date is reasonable (after 1900 and before 2100)
        const year = date.getFullYear();
        if (year < 1900 || year > 2100) {
          console.warn('Date out of reasonable range in CustomDatePicker:', dateValue, 'Year:', year);
          return null;
        }
        
        return date;
      } catch (error) {
        console.error('Error parsing date in CustomDatePicker:', error, 'Input:', dateValue);
        return null;
      }
    };
    
    return (
      <div className="form-group">
        <label htmlFor={name} className="form-label">
          {label} <span className="text-danger">*</span>
        </label>
        <DatePicker
          id={name}
          selected={parseDate(formik.values[name])}
          onChange={(date) => formik.setFieldValue(name, date)}
          onBlur={() => formik.setFieldTouched(name, true)}
          className={`form-control ${hasError ? 'is-invalid' : ''}`}
          placeholderText={`Select ${label.toLowerCase()}`}
          dateFormat="MM/dd/yyyy"
          minDate={name === 'startDate' ? new Date() : formik.values.startDate || new Date()}
          maxDate={new Date(2100, 11, 31)}
          showYearDropdown
          showMonthDropdown
          dropdownMode="select"
          {...props}
        />
        {hasError && (
          <div className="invalid-feedback d-block">
            {formik.errors[name]}
          </div>
        )}
      </div>
    );
  };

  if (loading && !currentCompany) {
    return (
      <BasicModal isModalOpen={displayAddModal} setIsModalOpen={setDisplayAddModal}>
        <div className="d-flex justify-content-center align-items-center" style={{ height: '200px' }}>
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      </BasicModal>
    );
  }

  return (
    <BasicModal
      isModalOpen={displayAddModal}
      setIsModalOpen={setDisplayAddModal}
      cancel={true}
    >
      {showTable ? (
        <PassengersList
          handleSelectRows={handleSelectRows}
          setShowTable={setShowTable}
          selectedPassengers={selectedPassengers}
        />
      ) : (
        <div className="container-fluid">
          <div className="row">
            <div className="col-12">
              <div className="d-flex justify-content-between align-items-center mb-4">
                <h4 className="modal-title mb-0">Add Subscriber</h4>
                {currentCompany && (
                  <span className="badge bg-info">
                    Company Balance: {currentCompany.balance}
                  </span>
                )}
              </div>

              {errorMsg && (
                <Alert variant="danger" dismissible onClose={() => setErrorMsg("")}>
                  {errorMsg}
                </Alert>
              )}

              <form onSubmit={formik.handleSubmit} noValidate>
                <div className="row">
                  <div className="col-lg-8 col-md-12">
                    <div className="card">
                      <div className="card-body">
                        {/* Passenger Selection */}
                        <div className="form-group mb-3">
                          <label className="form-label">
                            Passengers <span className="text-danger">*</span>
                          </label>
                          <div className="d-flex align-items-center gap-3">
                            <Button
                              variant="outline-secondary"
                              onClick={() => setShowTable(true)}
                              disabled={loading}
                            >
                              Select Passengers
                            </Button>
                            <span className="badge bg-secondary">
                              {selectedPassengers.length} selected
                            </span>
                          </div>
                          {formik.touched.passengerId && formik.errors.passengerId && (
                            <div className="text-danger small mt-1">
                              {formik.errors.passengerId}
                            </div>
                          )}
                        </div>

                        {/* Usage Limit */}
                        <div className="form-group mb-3">
                          <label htmlFor="usageLimit" className="form-label">
                            Usage Limit <span className="text-danger">*</span>
                          </label>
                          <input
                            type="number"
                            id="usageLimit"
                            name="usageLimit"
                            className={`form-control ${
                              formik.touched.usageLimit && formik.errors.usageLimit
                                ? 'is-invalid'
                                : ''
                            }`}
                            value={formik.values.usageLimit}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            placeholder="Enter usage limit"
                            min="0"
                            max={currentCompany?.balance || 0}
                          />
                          {formik.touched.usageLimit && formik.errors.usageLimit && (
                            <div className="invalid-feedback">
                              {formik.errors.usageLimit}
                            </div>
                          )}
                        </div>

                        {/* Date Pickers */}
                        <div className="row">
                          <div className="col-md-6">
                            <CustomDatePicker
                              name="startDate"
                              label="Start Date"
                            />
                          </div>
                          <div className="col-md-6">
                            <CustomDatePicker
                              name="endDate"
                              label="End Date"
                            />
                          </div>
                        </div>

                        {/* Status */}
                        <div className="form-group mb-3">
                          <label htmlFor="status" className="form-label">
                            Status <span className="text-danger">*</span>
                          </label>
                          <select
                            id="status"
                            name="status"
                            className={`form-select ${
                              formik.touched.status && formik.errors.status
                                ? 'is-invalid'
                                : ''
                            }`}
                            value={formik.values.status}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                          >
                            <option value="">Select Status</option>
                            <option value="Active">Active</option>
                            <option value="Inactive">Inactive</option>
                          </select>
                          {formik.touched.status && formik.errors.status && (
                            <div className="invalid-feedback">
                              {formik.errors.status}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Modal Footer */}
                <div className="d-flex justify-content-end gap-2 mt-4">
                  <Button
                    variant="secondary"
                    onClick={handleCancel}
                    disabled={loading}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="success"
                    disabled={loading || formik.isSubmitting}
                  >
                    {loading ? (
                      <>
                        <Spinner
                          as="span"
                          animation="border"
                          size="sm"
                          role="status"
                          aria-hidden="true"
                          className="me-2"
                        />
                        Submitting...
                      </>
                    ) : (
                      'Add Subscriber'
                    )}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </BasicModal>
  );
};

export default AddSubscriber;